// Pure form model + step config for the application wizard (no React/network).
// Field values are strings; enum values match the server's so no payload mapping.

export interface LocationEntry {
  name: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  deliveryNotes: string;
  isPrimary: boolean;
  isHeadquarters: boolean;
  // When true, this location copies the previous address (the main office for the
  // first location, the prior location otherwise) and its inputs are disabled.
  sameAsPrevious: boolean;
}

export interface PrincipalEntry {
  fullName: string;
  title: string;
  percentOwned: string;
  homeLine1: string;
  homeLine2: string;
  homeCity: string;
  homeState: string;
  homeZip: string;
  ssn: string;
  // On a resubmit, an SSN already stored for this principal — so SSN isn't
  // required and the field shows a "leave blank to keep ••••NNNN" hint.
  ssnOnFile: boolean;
  ssnLast4: string;
}

export interface TradeRefEntry {
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  line1: string;
  city: string;
  state: string;
  zip: string;
}

export interface TeamMemberEntry {
  name: string;
  email: string;
  role: string;
  locationAccess: number[];
}

export interface ApplicationForm {
  // Business
  legalName: string;
  dba: string;
  businessType: string;
  entityType: string;
  yearIncorporated: string;
  stateOfIncorporation: string;
  ein: string;
  officeLine1: string;
  officeLine2: string;
  officeCity: string;
  officeState: string;
  officeZip: string;
  officePhone: string;
  officeFax: string;
  officeWebsite: string;
  officeIsPrimary: boolean;
  officeIsHeadquarters: boolean;
  // Account owner / login
  ownerFirstName: string;
  ownerLastName: string;
  ownerTitle: string;
  ownerMobile: string;
  ownerEmail: string;
  password: string;
  passwordConfirm: string;
  heardAboutUs: string;
  heardAboutUsDetail: string;
  // Billing
  billingContact: string;
  billingPhone: string;
  billingEmail: string;
  invoiceMethod: string;
  // Delivery
  locations: LocationEntry[];
  // Credit
  creditLine: string;
  annualTonsRange: string;
  principals: PrincipalEntry[];
  references: TradeRefEntry[];
  // "See attached" — a reference sheet that waives the three structured references.
  tradeReferenceFileName: string;
  bankName: string;
  bankPhone: string;
  bankLine1: string;
  bankCity: string;
  bankState: string;
  bankZip: string;
  bankContact: string;
  bankAccountType: string;
  hasBankruptcy: '' | 'yes' | 'no';
  bankruptcyExplanation: string;
  // Team
  teamMembers: TeamMemberEntry[];
  // Products
  productInterestIds: string[];
  // Tax / resale certificate — captured as text; the PDF is generated from it.
  taxExempt: '' | 'yes' | 'no';
  resaleCertState: string;
  resaleSellerType: string;
  resaleInBusinessOf: string;
  resaleRegistrationNumber: string;
  resaleDescription: string;
  // Terms
  agreedToTerms: boolean;
  agreedPersonalGuarantee: boolean;
  signerIndex: string; // index into principals[]
  signerTitle: string;
  signerTypedSignature: string;
  // Review
  notRobot: boolean;
}

export const blankLocation = (): LocationEntry => ({
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  zip: '',
  deliveryNotes: '',
  isPrimary: false,
  isHeadquarters: false,
  sameAsPrevious: false,
});

export const blankPrincipal = (): PrincipalEntry => ({
  fullName: '',
  title: '',
  percentOwned: '',
  homeLine1: '',
  homeLine2: '',
  homeCity: '',
  homeState: '',
  homeZip: '',
  ssn: '',
  ssnOnFile: false,
  ssnLast4: '',
});

export const blankTradeRef = (): TradeRefEntry => ({
  companyName: '',
  contactName: '',
  phone: '',
  email: '',
  line1: '',
  city: '',
  state: '',
  zip: '',
});

export const blankTeamMember = (): TeamMemberEntry => ({
  name: '',
  email: '',
  role: 'PLACE_ORDERS',
  locationAccess: [],
});

export const blankForm = (): ApplicationForm => ({
  legalName: '',
  dba: '',
  businessType: '',
  entityType: '',
  yearIncorporated: '',
  stateOfIncorporation: '',
  ein: '',
  officeLine1: '',
  officeLine2: '',
  officeCity: '',
  officeState: '',
  officeZip: '',
  officePhone: '',
  officeFax: '',
  officeWebsite: '',
  officeIsPrimary: false,
  officeIsHeadquarters: false,
  ownerFirstName: '',
  ownerLastName: '',
  ownerTitle: '',
  ownerMobile: '',
  ownerEmail: '',
  password: '',
  passwordConfirm: '',
  heardAboutUs: '',
  heardAboutUsDetail: '',
  billingContact: '',
  billingPhone: '',
  billingEmail: '',
  invoiceMethod: 'Email',
  locations: [blankLocation()],
  creditLine: '',
  annualTonsRange: '',
  principals: [blankPrincipal()],
  references: [blankTradeRef(), blankTradeRef(), blankTradeRef()],
  tradeReferenceFileName: '',
  bankName: '',
  bankPhone: '',
  bankLine1: '',
  bankCity: '',
  bankState: '',
  bankZip: '',
  bankContact: '',
  bankAccountType: 'Business checking',
  hasBankruptcy: '',
  bankruptcyExplanation: '',
  teamMembers: [],
  productInterestIds: [],
  taxExempt: '',
  resaleCertState: '',
  resaleSellerType: '',
  resaleInBusinessOf: '',
  resaleRegistrationNumber: '',
  resaleDescription: '',
  agreedToTerms: false,
  agreedPersonalGuarantee: false,
  signerIndex: '',
  signerTitle: '',
  signerTypedSignature: '',
  notRobot: false,
});

export const isPrepay = (form: Pick<ApplicationForm, 'creditLine'>): boolean =>
  form.creditLine === 'PREPAY';

// Sum of ownership percentages across principals (blank/invalid count as 0).
// Rounded to 2 decimals so floating-point noise doesn't fail the 100% check.
export const totalOwnershipPercent = (
  principals: Pick<PrincipalEntry, 'percentOwned'>[]
): number => {
  const sum = principals.reduce((acc, p) => acc + (Number(p.percentOwned) || 0), 0);
  return Math.round(sum * 100) / 100;
};

// --- option lists (value = server enum / stored string; label = display) ---
export interface Option {
  value: string;
  label: string;
}

export const BUSINESS_TYPES: Option[] = [
  { value: 'LANDSCAPE_SUPPLY_YARD', label: 'Landscape supply yard' },
  { value: 'HARDSCAPE_MASONRY_CONTRACTOR', label: 'Hardscape / masonry contractor' },
  { value: 'GARDEN_CENTER', label: 'Garden center' },
  { value: 'BUILDING_SUPPLY', label: 'Building supply' },
  { value: 'GENERAL_CONTRACTOR', label: 'General contractor' },
  { value: 'OTHER', label: 'Other' },
];

export const ENTITY_TYPES: Option[] = [
  { value: 'LLC', label: 'LLC' },
  { value: 'CORP', label: 'Corporation' },
  { value: 'S_CORP', label: 'S-Corporation' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole proprietor' },
];

export const CREDIT_LINES: Option[] = [
  { value: 'CREDIT_5000', label: '$5,000' },
  { value: 'CREDIT_10000', label: '$10,000' },
  { value: 'CREDIT_25000', label: '$25,000' },
  { value: 'CREDIT_50000', label: '$50,000' },
  { value: 'CREDIT_50000_PLUS', label: '$50,000+' },
  { value: 'PREPAY', label: 'Prepay (no credit)' },
];

// Single source of truth for a credit-line label (used by the wizard review +
// the admin queue, so they can't drift).
export const creditLineLabel = (value: string | null | undefined): string =>
  CREDIT_LINES.find((o) => o.value === value)?.label ?? '';

// Business types a resale certificate asks for (the "type of business" field).
export const RESALE_SELLER_TYPES: Option[] = [
  { value: 'Retailer', label: 'Retailer' },
  { value: 'Wholesaler', label: 'Wholesaler' },
  { value: 'Contractor', label: 'Contractor' },
  { value: 'Manufacturer', label: 'Manufacturer' },
  { value: 'Lessor', label: 'Lessor' },
  { value: 'Other', label: 'Other' },
];

export const TONS_RANGES: Option[] = [
  { value: 'Under 1,000 tons', label: 'Under 1,000 tons' },
  { value: '1,000–5,000 tons', label: '1,000–5,000 tons' },
  { value: '5,000–10,000 tons', label: '5,000–10,000 tons' },
  { value: '10,000–25,000 tons', label: '10,000–25,000 tons' },
  { value: 'Over 25,000 tons', label: 'Over 25,000 tons' },
];

export const HEARD_OPTIONS: Option[] = [
  { value: 'Google or other search', label: 'Google or other search' },
  { value: 'Referral', label: 'Referral from a customer or contractor' },
  { value: 'DeFeo sales rep', label: 'DeFeo sales rep' },
  { value: 'Saw our trucks or a jobsite', label: 'Saw our trucks or a jobsite' },
  { value: 'Social media', label: 'Social media' },
  { value: 'Trade show or event', label: 'Trade show or event' },
  { value: 'Other', label: 'Other' },
];

export const TEAM_ROLES: Option[] = [
  { value: 'PLACE_ORDERS', label: 'Can place orders' },
  { value: 'VIEW_ORDERS_ONLY', label: 'View orders only' },
  { value: 'INVOICES_PAYMENTS_ONLY', label: 'Invoices & payments only' },
];

export const US_STATES =
  'AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY'.split(
    ' '
  );

// --- step / section config (mirrors the prototype) ---
export type StepId =
  | 'business-name'
  | 'business-type'
  | 'business-entity'
  | 'business-office'
  | 'owner'
  | 'verify'
  | 'billing'
  | 'locations'
  | 'credit'
  | 'principals'
  | 'bank'
  | 'references'
  | 'bankruptcy'
  | 'team'
  | 'products'
  | 'tax'
  | 'terms'
  | 'review';

export interface StepDef {
  id: StepId;
  section: string;
  title: string;
  help?: string;
  // Account creation happens once we reach the verify step.
  applicable?: (form: ApplicationForm) => boolean;
}

export const SECTIONS = [
  'Business',
  'You',
  'Billing',
  'Delivery',
  'Credit',
  'Team',
  'Products',
  'Tax',
  'Terms',
  'Review',
];

const creditOnly = (form: ApplicationForm) => !isPrepay(form);

export const STEPS: StepDef[] = [
  {
    id: 'business-name',
    section: 'Business',
    title: "What's your company's legal name?",
    help: 'As it appears on your tax filings.',
  },
  { id: 'business-type', section: 'Business', title: 'What kind of business are you?' },
  { id: 'business-entity', section: 'Business', title: 'How is the business set up?' },
  { id: 'business-office', section: 'Business', title: 'Where is your main office?' },
  {
    id: 'owner',
    section: 'You',
    title: "Who's setting up the account?",
    help: "You'll be the account owner and can add your team in a later step.",
  },
  { id: 'verify', section: 'You', title: 'Check your email' },
  { id: 'billing', section: 'Billing', title: 'Where should we send invoices?' },
  {
    id: 'locations',
    section: 'Delivery',
    title: 'Where should we deliver?',
    help: 'Add every yard or store that takes deliveries, and anything our drivers need to know.',
  },
  {
    id: 'credit',
    section: 'Credit',
    title: 'How much credit are you requesting?',
    help: 'Prefer to pay before each delivery? Choose prepay and you can skip the bank and reference questions.',
  },
  {
    id: 'principals',
    section: 'Credit',
    title: 'Who are the owners?',
    help: 'List each owner or officer of the business.',
  },
  { id: 'bank', section: 'Credit', title: 'Where do you bank?', applicable: creditOnly },
  {
    id: 'references',
    section: 'Credit',
    title: 'Give us three trade references.',
    help: 'Suppliers you buy from on terms. All three are required.',
    applicable: creditOnly,
  },
  {
    id: 'bankruptcy',
    section: 'Credit',
    title: 'Have you or any affiliated organization ever filed for bankruptcy?',
    applicable: creditOnly,
  },
  {
    id: 'team',
    section: 'Team',
    title: 'Who else on your team needs access?',
    help: "Optional. Each person gets their own login once you're approved.",
  },
  {
    id: 'products',
    section: 'Products',
    title: 'Which products are you interested in?',
    help: 'Pick everything you expect to order. Your rep will confirm pricing.',
  },
  {
    id: 'tax',
    section: 'Tax',
    title: 'Are you tax-exempt?',
    help: 'If yes, attach your resale certificate.',
  },
  { id: 'terms', section: 'Terms', title: 'Review and sign.' },
  { id: 'review', section: 'Review', title: 'Review your application' },
];

export const applicableSteps = (form: ApplicationForm): StepDef[] =>
  STEPS.filter((s) => !s.applicable || s.applicable(form));
